import { useState } from "react";
import { useTabUfIcms } from "@/features/fiscal/hooks/useFiscal";
import type { TabUfIcmsDto, SalvarTabUfIcmsRequest } from "@/features/fiscal/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { MapPin, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { AvisoTabelaNacional } from "@/components/AvisoTabelaNacional";

// ── Constantes ────────────────────────────────────────────────────────────
const UFS = [
  "AC","AL","AM","AP","BA","CE","DF","ES","GO","MA",
  "MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN",
  "RO","RR","RS","SC","SE","SP","TO",
] as const;

type UF = typeof UFS[number];
type UfKey = Lowercase<UF>;

const EMPTY_ALIQUOTAS: Record<UfKey, number> = {
  ac:0, al:0, am:0, ap:0, ba:0, ce:0, df:0, es:0, go:0, ma:0,
  mg:0, ms:0, mt:0, pa:0, pb:0, pe:0, pi:0, pr:0, rj:0, rn:0,
  ro:0, rr:0, rs:0, sc:0, se:0, sp:0, to:0,
};

const EMPTY: SalvarTabUfIcmsRequest = {
  ...EMPTY_ALIQUOTAS,
  perIcmsSt: 0,
  perFcp:    0,
  perFcpSt:  0,
};

function dtoToForm(dto: TabUfIcmsDto): SalvarTabUfIcmsRequest {
  return {
    ac: dto.ac, al: dto.al, am: dto.am, ap: dto.ap, ba: dto.ba, ce: dto.ce,
    df: dto.df, es: dto.es, go: dto.go, ma: dto.ma, mg: dto.mg, ms: dto.ms,
    mt: dto.mt, pa: dto.pa, pb: dto.pb, pe: dto.pe, pi: dto.pi, pr: dto.pr,
    rj: dto.rj, rn: dto.rn, ro: dto.ro, rr: dto.rr, rs: dto.rs, sc: dto.sc,
    se: dto.se, sp: dto.sp, to: dto.to,
    perIcmsSt: dto.perIcmsSt,
    perFcp:    dto.perFcp,
    perFcpSt:  dto.perFcpSt,
  };
}

export default function TributosUfDestino() {
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [editUf, setEditUf]             = useState<string | null>(null);
  const [novaUf, setNovaUf]             = useState<string>("");
  const [form, setForm]                 = useState<SalvarTabUfIcmsRequest>(EMPTY);
  const [saving, setSaving]             = useState(false);

  const { user } = useAuth();
  const podeEditar = user?.isAdminGlobal ?? false;

  const { query, criar, atualizar } = useTabUfIcms();
  const itens: TabUfIcmsDto[] = query.data ?? [];

  // UFs ainda não configuradas (para o select "Novo")
  const ufsConfiguradas = new Set(itens.map(i => i.ufOrigem?.toUpperCase()));
  const ufsDisponiveis  = UFS.filter(uf => !ufsConfiguradas.has(uf));

  // ── Helpers ─────────────────────────────────────────────────────────────
  const setAliq = (uf: UfKey, raw: string) =>
    setForm(f => ({ ...f, [uf]: parseFloat(raw) || 0 }));

  const setPct = (field: "perIcmsSt" | "perFcp" | "perFcpSt", raw: string) =>
    setForm(f => ({ ...f, [field]: parseFloat(raw) || 0 }));

  const openEdit = (dto: TabUfIcmsDto) => {
    setEditUf(dto.ufOrigem ?? "");
    setForm(dtoToForm(dto));
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditUf(null);
    setNovaUf(ufsDisponiveis[0] ?? "");
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const uf = editUf ?? novaUf;
    if (!uf) { toast.error("Selecione a UF de origem."); return; }

    setSaving(true);
    try {
      if (editUf) {
        await atualizar.mutateAsync({ ufOrigem: editUf, req: form });
        toast.success(`Alíquotas de ${editUf} atualizadas.`);
      } else {
        await criar.mutateAsync({ ufOrigem: novaUf, req: form });
        toast.success(`Configuração de ${novaUf} criada.`);
      }
      setDialogOpen(false);
    } catch {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MapPin className="h-6 w-6" /> Tributos por UF de Destino
          </h1>
          <p className="text-muted-foreground">Alíquotas interestaduais de ICMS por estado de origem</p>
        </div>
        {podeEditar && (
          <Button onClick={openNew} disabled={ufsDisponiveis.length === 0}>
            <Plus className="h-4 w-4 mr-2" /> Nova UF
          </Button>
        )}
      </div>

      {!podeEditar && <AvisoTabelaNacional />}

      {/* Tabela resumo */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">UF Origem</TableHead>
              <TableHead className="text-center w-[120px]">% Alíq. Interna</TableHead>
              <TableHead className="text-center w-[120px]">% FCP</TableHead>
              <TableHead className="text-center w-[120px]">% FCP ST</TableHead>
              <TableHead className="text-right w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : itens.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  Nenhuma UF configurada.
                </TableCell>
              </TableRow>
            ) : itens.map(item => (
              <TableRow key={item.ufOrigem}>
                <TableCell className="font-mono font-bold text-base">{item.ufOrigem ?? "—"}</TableCell>
                <TableCell className="text-center font-semibold">
                  {((item as unknown as Record<string, number>)[(item.ufOrigem ?? "").toLowerCase()] ?? 0).toFixed(2)}%
                </TableCell>
                <TableCell className="text-center">{item.perFcp.toFixed(2)}%</TableCell>
                <TableCell className="text-center">{item.perFcpSt.toFixed(2)}%</TableCell>
                <TableCell className="text-right">
                  {podeEditar && (
                    <Button size="icon" variant="ghost" onClick={() => openEdit(item)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editUf
                ? `Alíquotas ICMS — UF de Origem: ${editUf}`
                : "Nova Configuração de UF"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* Seleção de UF (somente novo) */}
            {!editUf && (
              <div className="space-y-1 max-w-xs">
                <Label>UF de Origem <span className="text-destructive">*</span></Label>
                <Select value={novaUf} onValueChange={setNovaUf}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ufsDisponiveis.map(uf => (
                      <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Percentuais gerais */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>% ICMS ST</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.perIcmsSt}
                  onChange={e => setPct("perIcmsSt", e.target.value)}
                  disabled={!podeEditar}
                  readOnly={!podeEditar}
                />
              </div>
              <div className="space-y-1">
                <Label>% FCP</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.perFcp}
                  onChange={e => setPct("perFcp", e.target.value)}
                  disabled={!podeEditar}
                  readOnly={!podeEditar}
                />
              </div>
              <div className="space-y-1">
                <Label>% FCP ST</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.perFcpSt}
                  onChange={e => setPct("perFcpSt", e.target.value)}
                  disabled={!podeEditar}
                  readOnly={!podeEditar}
                />
              </div>
            </div>

            <Separator />

            {/* Matriz de alíquotas por UF de destino */}
            <div>
              <p className="text-sm font-semibold mb-1">Alíquota ICMS por UF de Destino</p>
              <p className="text-xs text-muted-foreground mb-3">
                A célula realçada (própria UF) é a <span className="font-semibold text-foreground">alíquota interna</span> do estado.
              </p>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7">
                {UFS.map(uf => {
                  const key = uf.toLowerCase() as UfKey;
                  const isInterna = uf === (editUf ?? novaUf);
                  return (
                    <div
                      key={uf}
                      className={
                        isInterna
                          ? "space-y-0.5 rounded-md bg-primary/10 p-1 -m-1"
                          : "space-y-0.5"
                      }
                    >
                      <Label className="text-xs font-mono text-center block flex items-center justify-center gap-1">
                        {uf}
                        {isInterna && (
                          <span className="rounded bg-primary px-1 text-[9px] font-semibold leading-tight text-primary-foreground">
                            interna
                          </span>
                        )}
                      </Label>
                      <Input
                        className={
                          isInterna
                            ? "h-8 text-center text-sm px-1 ring-2 ring-primary font-semibold"
                            : "h-8 text-center text-sm px-1"
                        }
                        type="number"
                        step="0.01"
                        min="0"
                        value={form[key]}
                        onChange={e => setAliq(key, e.target.value)}
                        disabled={!podeEditar}
                        readOnly={!podeEditar}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            {podeEditar && (
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
